import { Navigate } from 'react-router-dom'
import { useProject } from '@/hooks/use-project'
import { useAuth } from '@/hooks/use-auth'
import { useDevices } from '@/hooks/use-devices'
import { timeAgo } from '@/lib/utils'
import { buildAppHref } from '@/lib/auth'
import { formatBrowserDateTime, parseApiDateTime } from '@/lib/format'
import { Smartphone, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SightingsSparkline } from '@/components/sightings-sparkline'

export function DevicesPage() {
  const auth = useAuth()
  const project = useProject()
  const { data: devices, isLoading, error } = useDevices(project)

  if (!project) {
    return (
      <Navigate
        to={auth?.mode === 'admin' ? buildAppHref('/admin', { mode: 'admin', projectCode: null }) : '/'}
        replace
      />
    )
  }

  const allDevices = devices ?? []

  return (
    <div className="mx-auto max-w-[1200px] px-8 py-8">
      <Card className="bg-card/95 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-3xl">
            <Smartphone className="h-8 w-8" /> Device Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="py-12 text-center text-muted-foreground text-lg">
              <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading devices...
            </div>
          )}

          {error && (
            <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
              Error loading devices: {(error as Error).message}
            </div>
          )}

          {devices && (
            <>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-primary hover:bg-primary">
                      <TableHead className="text-primary-foreground font-semibold">Device</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Name</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Rego</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Hourly Detections</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Status</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Last Detection</TableHead>
                      <TableHead className="text-primary-foreground font-semibold">Activity (90 min)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allDevices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">No devices found</TableCell>
                      </TableRow>
                    ) : (
                      allDevices.map((device) => {
                        const connectedAt = parseApiDateTime(device.when)
                        const lastDetectionAt = parseApiDateTime(device.last_detection)

                        return (
                          <TableRow key={device.id}>
                            <TableCell className="font-medium">
                              {device.location || `Device ${device.site_no}`}
                            </TableCell>
                          <TableCell>{device.name || '—'}</TableCell>
                          <TableCell>{device.rego || '—'}</TableCell>
                          <TableCell>{device.no_of_sightings}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className={`inline-block h-3 w-3 rounded-full shrink-0 ${device.bt_device === 0 ? 'bg-destructive' : 'bg-success'}`} />
                              <div>
                                {connectedAt ? (
                                  <>
                                    {device.bt_device === 0 ? 'Disconnected' : 'Connected'} — {timeAgo(connectedAt)}
                                    <br />
                                    <span className="text-xs text-muted-foreground">{formatBrowserDateTime(device.when)}</span>
                                  </>
                                ) : (
                                  <>
                                    <span>{device.bt_device === 0 ? 'Disconnected' : 'Connected'}</span>
                                    <br />
                                    <span className="text-xs text-muted-foreground">Never</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {lastDetectionAt ? (
                              <>
                                {timeAgo(lastDetectionAt)}
                                <br />
                                <span className="text-xs text-muted-foreground">{formatBrowserDateTime(device.last_detection)}</span>
                              </>
                            ) : 'Never'}
                          </TableCell>
                          <TableCell>
                            <SightingsSparkline data={device.sightings_sparkline} />
                          </TableCell>
                        </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
